import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { X, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR } from '../lib/format';
import { computeDocument, MODES } from '../lib/pricing';
import AmPmTimePicker from '../components/AmPmTimePicker';

function newLine(from = {}) {
  return { id: crypto.randomUUID(), name: '', sub: '', qty: 1, rate: 0, pricing_mode: 'standard', discount_value: 0, reason_code: '', ...from };
}
function newGroup(name = 'Wedding') {
  return { id: crypto.randomUUID(), name, date: '', reporting_time: '', ready_by_time: '', venue: '', services: [newLine()], travel_lines: [] };
}

export default function InvoiceEdit() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const projId = search.get('project');
  const nav = useNavigate();

  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [terms, setTerms] = useState([]);
  const [settings, setSettings] = useState(null);

  const [f, setF] = useState({
    client_name: '', client_phone: '', client_address: '', project_id: projId || '',
    event_date: '', reporting_time: '', ready_by_time: '', venue: '',
    event_groups: [newGroup()],
    includes: [], terms: [], discount: 0, advance_pct: 30, advance_received: 0,
    status: 'awaiting_advance', needs_confirmation: false, confirmation_note: '',
    thank_you_note: '', balance_due_note: '',
  });

  useEffect(() => {
    Promise.all([
      api.get('/projects').then(r => setProjects(r.data)),
      api.get('/services').then(r => setServices(r.data.filter(x => x.active).sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))),
      api.get('/reason-codes').then(r => setReasons(r.data.sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))),
      api.get('/terms').then(r => setTerms(r.data.sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))),
      api.get('/settings').then(r => setSettings(r.data)),
    ]);
    if (id) api.get(`/invoices/${id}`).then(r => {
      const d = r.data;
      // Migrate legacy flat services -> single event_group
      if ((!d.event_groups || !d.event_groups.length) && d.services?.length) {
        d.event_groups = [{
          id: crypto.randomUUID(), name: 'Event', date: d.event_date, reporting_time: d.reporting_time,
          ready_by_time: d.ready_by_time, venue: d.venue,
          services: d.services.map(s => newLine({ name: s.name, sub: s.sub, qty: s.qty, rate: s.was || s.rate, pricing_mode: s.was ? 'flat' : 'standard', discount_value: s.was ? (s.was - s.rate) : 0 })),
          travel_lines: []
        }];
      }
      setF(d);
    });
  }, [id]);

  // Apply settings defaults on new invoice
  useEffect(() => {
    if (!id && settings && !f.thank_you_note) {
      setF(x => ({
        ...x,
        thank_you_note: settings.document_text?.thank_you_default || '',
        balance_due_note: settings.document_text?.balance_due_note_default || '',
        advance_pct: settings.default_advance_pct || 30,
        terms: terms.filter(t => t.on_invoice).map(t => ({ title: t.title, body: t.body })),
      }));
    }
  }, [settings, terms, id]);

  const comp = computeDocument(f.event_groups, [], f.discount);
  const total = comp.total;
  const balance = Math.max(total - (f.advance_received||0), 0);

  const updGroup = (gi, k, v) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], [k]: v }; setF({ ...f, event_groups: g }); };
  const updLine = (gi, li, k, v) => { const g = [...f.event_groups]; const s = [...g[gi].services]; s[li] = { ...s[li], [k]: v }; g[gi] = { ...g[gi], services: s }; setF({ ...f, event_groups: g }); };
  const addLine = (gi) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], services: [...(g[gi].services||[]), newLine()] }; setF({ ...f, event_groups: g }); };
  const rmLine = (gi, li) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], services: g[gi].services.filter((_,x) => x !== li) }; setF({ ...f, event_groups: g }); };
  const addGroup = () => setF({ ...f, event_groups: [...f.event_groups, newGroup('Reception')] });
  const rmGroup = (gi) => setF({ ...f, event_groups: f.event_groups.filter((_,x) => x !== gi) });
  const addTravel = (gi) => { const g = [...f.event_groups]; g[gi] = { ...g[gi], travel_lines: [...(g[gi].travel_lines||[]), { label: '', amount: 0 }] }; setF({ ...f, event_groups: g }); };

  const pickService = (gi, li, svcId) => {
    const svc = services.find(s => s.id === svcId);
    if (!svc) return;
    updLine(gi, li, 'name', svc.name);
    updLine(gi, li, 'sub', svc.description || '');
    updLine(gi, li, 'rate', svc.default_rate);
    // aggregate inclusions across all picked services
    const g = f.event_groups[gi];
    const allIncl = [...new Set([...(f.includes||[]), ...(svc.inclusions||[])])];
    setF({ ...f, includes: allIncl });
  };

  const save = async () => {
    // Snapshot single-event fields from first group for compatibility + legacy `services`
    const first = f.event_groups?.[0] || {};
    const flatServices = (f.event_groups || []).flatMap(g => (g.services || []).map(l => {
      const base = (l.qty||0) * (l.rate||0);
      const c = computeDocument([{ services: [l] }]);
      const line = c.enriched[0].services[0];
      return { name: l.name, sub: l.sub, qty: l.qty, rate: line.now, was: line.saving > 0 ? base : null, reason_code: l.reason_code, is_complimentary: line.is_complimentary };
    }));
    const body = {
      ...f,
      services: flatServices,
      event_date: first.date || f.event_date,
      reporting_time: first.reporting_time || f.reporting_time,
      ready_by_time: first.ready_by_time || f.ready_by_time,
      venue: first.venue || f.venue,
      subtotal: comp.subtotal,
      total, discount: f.discount,
      value_given_away: comp.valueGivenAway,
      balance,
      status: balance <= 0 && (f.advance_received||0) > 0 ? 'fully_paid' : (f.advance_received||0) > 0 ? 'partially_paid' : 'awaiting_advance',
    };
    try {
      let r;
      if (id) r = await api.put(`/invoices/${id}`, body);
      else r = await api.post('/invoices', body);
      toast.success('Invoice saved');
      nav(`/invoices/${(r.data.id || id)}`);
    } catch { toast.error('Save failed'); }
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-4xl mx-auto">
      <Link to="/invoices" className="eyebrow text-[var(--gold)]">← Invoices</Link>
      <h1 className="serif text-[28px] mt-2 mb-5">{id ? 'Edit Invoice' : 'New Invoice'}</h1>

      <div className="card-cream p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="eyebrow">Client name</label><input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} data-testid="inv-form-client"/></div>
          <div><label className="eyebrow">Phone</label><input value={f.client_phone} onChange={e => setF({ ...f, client_phone: e.target.value })}/></div>
        </div>
        <div><label className="eyebrow">Address</label><input value={f.client_address} onChange={e => setF({ ...f, client_address: e.target.value })}/></div>
        <div><label className="eyebrow">Project (optional)</label>
          <select value={f.project_id} onChange={e => setF({ ...f, project_id: e.target.value })}>
            <option value="">—</option>{projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Event groups */}
      <div className="mt-5 flex items-center justify-between">
        <div className="eyebrow">Events & Line Items</div>
        <button onClick={addGroup} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid="inv-add-group"><Plus size={12} className="inline"/> Add Date/Event</button>
      </div>
      <div className="space-y-4 mt-3">
        {(f.event_groups||[]).map((g, gi) => (
          <EventGroupEditor key={g.id} g={g} gi={gi} services={services} reasons={reasons} settings={settings}
            update={(k,v) => updGroup(gi, k, v)}
            updateLine={(li, k, v) => updLine(gi, li, k, v)}
            addLine={() => addLine(gi)}
            rmLine={(li) => rmLine(gi, li)}
            pickService={(li, svcId) => pickService(gi, li, svcId)}
            addTravel={() => addTravel(gi)}
            onRemove={() => rmGroup(gi)}
            canRemove={f.event_groups.length > 1}
          />
        ))}
      </div>

      {/* Overall */}
      <div className="card-cream p-5 mt-4 grid grid-cols-2 gap-3">
        <div><label className="eyebrow">Package discount (₹)</label><input type="number" value={f.discount} onChange={e => setF({ ...f, discount: parseFloat(e.target.value||0) })}/></div>
        <div><label className="eyebrow">Advance %</label><input type="number" value={f.advance_pct} onChange={e => setF({ ...f, advance_pct: parseInt(e.target.value||0) })}/></div>
        <div><label className="eyebrow">Advance received (₹)</label><input type="number" value={f.advance_received} onChange={e => setF({ ...f, advance_received: parseFloat(e.target.value||0) })}/></div>
        <div><label className="eyebrow">Balance note</label><input value={f.balance_due_note} onChange={e => setF({ ...f, balance_due_note: e.target.value })}/></div>
      </div>

      <div className="card-cream p-5 mt-4">
        <label className="flex items-center gap-2 text-sm mb-2"><input type="checkbox" style={{width:'auto'}} checked={f.needs_confirmation} onChange={e => setF({ ...f, needs_confirmation: e.target.checked })}/> Needs confirmation</label>
        {f.needs_confirmation && <input placeholder="What needs confirming?" value={f.confirmation_note} onChange={e => setF({ ...f, confirmation_note: e.target.value })}/>}
        <label className="eyebrow mt-3 block">Thank-you note</label>
        <input value={f.thank_you_note} onChange={e => setF({ ...f, thank_you_note: e.target.value })}/>
      </div>

      <div className="card-alt p-5 mt-4">
        <div className="flex justify-between text-sm py-1"><span>Subtotal</span><span className="mono-num">{formatINR(comp.subtotal)}</span></div>
        {(comp.subtotal - comp.discountedGross) > 0 && <div className="flex justify-between text-sm py-1" style={{color:'var(--gold)'}}><span>Line discounts + comps</span><span className="mono-num">− {formatINR(comp.subtotal - comp.discountedGross)}</span></div>}
        {f.discount > 0 && <div className="flex justify-between text-sm py-1" style={{color:'var(--gold)'}}><span>Package discount</span><span className="mono-num">− {formatINR(f.discount)}</span></div>}
        <div className="flex justify-between text-lg py-2 font-medium border-t hairline mt-1"><span>Total</span><span className="serif mono-num">{formatINR(total)}</span></div>
        <div className="flex justify-between text-sm mt-1" style={{color:'var(--pending)'}}><span>Balance</span><span className="mono-num">{formatINR(balance)}</span></div>
        {comp.valueGivenAway > 0 && <div className="flex justify-between text-xs pt-2 border-t hairline mt-2 text-[var(--muted)]"><span>{settings?.document_text?.value_given_line || 'Total value included at no charge'}</span><span className="mono-num">{formatINR(comp.valueGivenAway)}</span></div>}
      </div>

      <button className="btn-gold w-full mt-5" onClick={save} data-testid="inv-form-save">{id ? 'Save Changes' : 'Create Invoice'}</button>
    </div>
  );
}

function EventGroupEditor({ g, gi, services, reasons, settings, update, updateLine, addLine, rmLine, pickService, addTravel, onRemove, canRemove }) {
  const eventTypes = settings?.event_types || ['Wedding','Reception'];
  return (
    <div className="card-cream p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <select value={g.name} onChange={e => update('name', e.target.value)} className="w-40">{eventTypes.map(t => <option key={t}>{t}</option>)}</select>
        <input type="date" value={g.date} onChange={e => update('date', e.target.value)} className="w-40"/>
        {canRemove && <button onClick={onRemove} className="text-[var(--overdue)] ml-auto"><Trash2 size={14}/></button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <AmPmTimePicker value={g.reporting_time} onChange={v => update('reporting_time', v)} label="Reporting time" testId={`grp-${gi}-report`}/>
        <AmPmTimePicker value={g.ready_by_time} onChange={v => update('ready_by_time', v)} label="Ready by" testId={`grp-${gi}-ready`}/>
        <div className="md:col-span-2"><label className="eyebrow">Venue</label><input value={g.venue} onChange={e => update('venue', e.target.value)}/></div>
      </div>

      {/* Line items */}
      <div className="space-y-2">
        {(g.services||[]).map((line, li) => (
          <LineItemEditor key={line.id} line={line} services={services} reasons={reasons}
            onChange={(k,v) => updateLine(li, k, v)}
            onPickService={svcId => pickService(li, svcId)}
            onRemove={() => rmLine(li)}
          />
        ))}
      </div>
      <div className="flex gap-4 mt-2">
        <button onClick={addLine} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid={`grp-${gi}-add-line`}>+ Line item</button>
        <button onClick={addTravel} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">+ Travel line</button>
      </div>

      {(g.travel_lines||[]).length > 0 && (
        <div className="mt-3 pt-3 border-t hairline">
          <div className="eyebrow mb-2">Travel</div>
          {(g.travel_lines||[]).map((t, ti) => (
            <div key={ti} className="grid grid-cols-[1fr_100px_20px] gap-2 mb-2">
              <input placeholder="e.g. Outstation road" value={t.label} onChange={e => { const tl = [...g.travel_lines]; tl[ti] = { ...tl[ti], label: e.target.value }; update('travel_lines', tl); }}/>
              <input type="number" placeholder="₹" value={t.amount} onChange={e => { const tl = [...g.travel_lines]; tl[ti] = { ...tl[ti], amount: parseFloat(e.target.value||0) }; update('travel_lines', tl); }}/>
              <button onClick={() => update('travel_lines', g.travel_lines.filter((_,x) => x !== ti))} className="text-[var(--overdue)]"><X size={12}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LineItemEditor({ line, services, reasons, onChange, onPickService, onRemove }) {
  const isComp = line.pricing_mode === 'complimentary';
  return (
    <div className="border-l-2 pl-3" style={{borderColor: isComp ? 'var(--gold)' : 'var(--line)'}}>
      <div className="grid grid-cols-[1fr_60px_100px_20px] gap-2 items-center mb-1">
        <div>
          <select value="" onChange={e => e.target.value && onPickService(e.target.value)} className="text-xs mb-1">
            <option value="">— Pick from catalogue —</option>
            {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.default_rate ? '₹'+s.default_rate : 'TBC'})</option>)}
          </select>
          <input placeholder="Service name" value={line.name} onChange={e => onChange('name', e.target.value)}/>
        </div>
        <input type="number" placeholder="Qty" value={line.qty} onChange={e => onChange('qty', parseInt(e.target.value||0))}/>
        <input type="number" placeholder="Rate" value={line.rate} onChange={e => onChange('rate', parseFloat(e.target.value||0))} disabled={isComp}/>
        <button onClick={onRemove} className="text-[var(--overdue)]"><X size={14}/></button>
      </div>
      <input placeholder="Sub description (printed on doc)" value={line.sub||''} onChange={e => onChange('sub', e.target.value)} className="text-xs mb-1"/>
      <div className="grid grid-cols-2 gap-2 mt-1">
        <select value={line.pricing_mode} onChange={e => onChange('pricing_mode', e.target.value)} className="text-xs">
          {MODES.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        {['flat','pct','per_head'].includes(line.pricing_mode) && (
          <input type="number" placeholder={line.pricing_mode==='pct'?'% off':'₹ off'} value={line.discount_value||0} onChange={e => onChange('discount_value', parseFloat(e.target.value||0))} className="text-xs"/>
        )}
        {isComp && (
          <select value={line.reason_code||''} onChange={e => onChange('reason_code', e.target.value)} className="text-xs">
            <option value="">— Reason (required) —</option>
            {reasons.map(r => <option key={r.id} value={r.code}>{r.label}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}
